import type { PrimaryFilterId } from '../../../filters/types/filters';
import { primaryDimensionFromFilterId } from '../../Chart/Table/utils/table-columns-to-query';
import type { PlotOptions, TableColumnConfig } from '../types/item';

const newId = (): string => crypto.randomUUID();

/**
 * Converts a legacy table config (unit-of-analysis + single segment + multiple
 * variables) into the per-column model. Legacy tables always rendered
 * primary-entity rows, optionally broken down by a single secondary dimension,
 * with one column per selected metric.
 */
const migrateTableColumns = (
  plot: PlotOptions | undefined,
  primaryFilterId: PrimaryFilterId
): TableColumnConfig[] => {
  const columns: TableColumnConfig[] = [
    {
      id: newId(),
      kind: 'dimension',
      dimension: primaryDimensionFromFilterId(primaryFilterId),
      alignment: 'left',
    },
  ];

  const secondary = plot?.secondaryDimension;
  if (secondary) {
    columns.push({
      id: newId(),
      kind: 'dimension',
      dimension: secondary,
      alignment: 'left',
    });
  }

  for (const metric of plot?.defaultMetricOptions ?? []) {
    columns.push({
      id: newId(),
      kind: 'variable',
      metric,
      aggregation: 'sum',
    });
  }

  return columns;
};

/**
 * Returns the table's column config, lazily migrating legacy config when
 * `tableColumns` has not yet been persisted. Safe to call on every render.
 */
export const getEffectiveTableColumns = (
  plot: PlotOptions | undefined,
  primaryFilterId: PrimaryFilterId
): TableColumnConfig[] => {
  return plot?.tableColumns && plot.tableColumns.length > 0
    ? plot.tableColumns
    : migrateTableColumns(plot, primaryFilterId);
};
